import React from 'react';
import { Video as LucideIcon, TrendingUp, TrendingDown } from 'lucide-react';

interface KPICardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  suffix?: string;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
}

const KPICard: React.FC<KPICardProps> = ({ title, value, icon: Icon, suffix, trend, trendValue }) => {
  const [displayValue, setDisplayValue] = React.useState(0);
  const numericValue = typeof value === 'number' ? value : parseInt(String(value)) || 0;

  React.useEffect(() => {
    if (numericValue === 0) {
      setDisplayValue(0);
      return;
    }

    let startTime: number | null = null;
    const duration = 1000;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);

      setDisplayValue(Math.floor(progress * numericValue));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [numericValue]);

  const getTrendIcon = () => {
    if (trend === 'up') return <TrendingUp size={14} className="text-emerald-600" />;
    if (trend === 'down') return <TrendingDown size={14} className="text-rose-600" />;
    return null;
  };

  const getTrendColor = () => {
    if (trend === 'up') return 'text-emerald-600';
    if (trend === 'down') return 'text-rose-600';
    return 'text-gray-500';
  };

  const isHighValue = numericValue > 0 && numericValue >= 5;

  return (
    <div className={`group bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl p-6 border-2 border-[#1875FF]/20 shadow-lg hover:shadow-2xl hover:border-[#05E0D1] transition-all duration-300 hover:-translate-y-1 ${isHighValue ? 'hover:shadow-[#05E0D1]/40' : ''}`}>
      <div className="flex flex-col items-center justify-center h-full space-y-4">
        <div
          className={`w-14 h-14 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-xl flex items-center justify-center group-hover:from-[#1875FF]/20 group-hover:to-[#05E0D1]/20 transition-all duration-300 group-hover:scale-110 shadow-md group-hover:shadow-lg`}
          style={isHighValue ? {
            animation: 'pulse-scale 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
          } : undefined}
        >
          <Icon className="text-[#1875FF] group-hover:text-[#05E0D1] transition-colors opacity-100" size={26} />
        </div>

        <div className="space-y-1 text-center">
          <div className={`text-4xl font-bold font-metric bg-clip-text text-transparent bg-gradient-to-r from-[#1875FF] to-[#05E0D1] transition-all duration-300`}>
            {numericValue === 0 ? '—' : `${displayValue}${suffix || ''}`}
          </div>
          <div className="text-sm font-semibold text-gray-700">{title}</div>
        </div>

        {trend && trendValue && (
          <div className={`flex items-center space-x-1 ${getTrendColor()}`}>
            {getTrendIcon()}
            <span className="text-xs font-semibold">{trendValue}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default KPICard;
